import { Either, Maybe, Validation } from 'monet';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import { DISCOUNT_IN_KOPECK } from '@/components/SwitchDiscountType.vue';
import LeftResponse from '@/storage/LeftResponse';
import { Category, PositionDiscount, PriceList } from './VisitGateway';
import ServicePriceRubles from '../ServicePriceRubles';

/**
 * @description Ожидаемый тип контента docx.
 * @type {string}
 */
const ACCEPT_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * @description Шаблон.
 * @param {number}                id                Идентификатор.
 * @param {boolean}               hasTrashed        Имеет удаленные услуги
 * @param {boolean}               isPrintLogo       Печатать логотип.
 * @param {string}                name              Название.
 * @param {PlanTemplateService[]} services          Услуги.
 * @param {PlanTemplateStage[]}   stages            Этапы.
 * @param {String}                categoryId        Id Категории.
 * @constructor
 */
function PlanTemplate(
  id,
  hasTrashed,
  isPrintLogo,
  name,
  services,
  stages,
  categoryId = '',
) {
  this.getId = () => id;
  this.getHasTrashed = () => hasTrashed;
  this.getIsPrintLogo = () => isPrintLogo;
  this.getName = () => name;
  this.getServices = () => services;
  this.getStages = () => stages;
  this.getCategoryId = () => categoryId;
  this.setStages = (newStages) => new this.constructor(
    id,
    hasTrashed,
    isPrintLogo,
    name,
    [],
    newStages,
    categoryId,
  );
}

/**
 * Категория плана лечения.
 *
 * @param id {Number}
 * @param name {String}
 * @constructor
 */
function PlanCategory(id, name) {
  this.id = id;
  this.name = name;
}

/**
 * @description Услуга шаблона.
 * @param {number}   count        Количество единиц.
 * @param {any[]}    dentalParts  Стоматологические части.
 * @param {number}   id           Идентификатор.
 * @param {string}   name         Название.
 * @param {number}   price        Стоимость.
 * @param {number[]} teeth        Номера зубов.
 * @param {string}   units        Единица измерения.
 * @param {string}   categoryName Имя категории.
 * @constructor
 */
function PlanTemplateService(count, dentalParts, id, name, price, teeth, units, categoryName) {
  this.getCount = () => count;
  this.getDentalParts = () => dentalParts;
  this.getId = () => id;
  this.getName = () => name;
  this.getPrice = () => price;
  this.getTeeth = () => teeth;
  this.getUnits = () => units;
  this.getCategoryName = () => categoryName;
}

/**
 * @description Этапы шаблона.
 * @param {number} id
 * @param {string} name
 * @param {array<PlanTemplateService>} services
 * @constructor
 */
export function PlanTemplateStage(
  id,
  name,
  services,
) {
  this.getId = () => id;
  this.getName = () => name;
  this.getServices = () => services;
}

/**
 * @description Элемент списка планов лечения пациента.
 * @param {string} authorName       Имя автора.
 * @param {string} authorSpeciality Специализация автора.
 * @param {string} branchName       Наименование филиала.
 * @param {number} id               Идентификатор.
 * @param {string} name             Название.
 * @param {string} date             Дата.
 * @param {string} price            Итоговая стоимость.
 * @param {string} statusId         Идентификатор статуса.
 * @param {boolean} hasServicesFromAnotherBranch Имеет услуги с других клиник
 * @constructor
 */
export function PlanItem(
  authorName,
  authorSpeciality,
  branchName,
  id,
  name,
  date,
  price,
  statusId,
  hasServicesFromAnotherBranch = false,
) {
  this.authorName = authorName;
  this.authorSpeciality = authorSpeciality;
  this.branchName = branchName;
  this.id = id;
  this.name = name;
  this.date = date;
  this.price = price;
  this.statusId = statusId;
  this.hasServicesFromAnotherBranch = hasServicesFromAnotherBranch;
}

/**
 * @description Услуга.
 * @param {boolean}     byVHIPolicy       Услуга оказана по полису ДМС.
 * @param {string}      categoryName      Название категории.
 * @param {number}      count             Количество.
 * @param {any[]}       dentalParts       Стоматологические части.
 * @param {number|null} id                Идентификатор.
 * @param {string}      key               Ключ.
 * @param {number}      price             Цена.
 * @param {string}      progress          Прогресс выполнения.
 * @param {number}      serviceId         Идентификатор услуги.
 * @param {string}      serviceName       Название услуги.
 * @param {number[]}    teeth             Зубы.
 * @param {string}      units             Ед. измерения.
 * @param {string}      visitDate         Дата визита.
 * @param {number}      visitId           Идентификатор визита.
 * @param {number}      visitNumber       Номер визита.
 * @constructor
 */
export function PlanService(
  byVHIPolicy = false,
  categoryName = '',
  count = 1,
  dentalParts = [],
  discount = PositionDiscount.default(),
  id = null,
  key = '',
  price = 0,
  progress = '',
  serviceId = null,
  serviceName = '',
  teeth = [],
  units = '',
  visitDate = '',
  visitId = null,
  visitNumber = '',
  order = null,
) {
  this.byVHIPolicy = byVHIPolicy;
  this.categoryName = categoryName;
  this.count = count;
  this.dentalParts = dentalParts;
  this.discount = discount;
  this.id = id;
  this.key = key;
  this.price = price;
  this.progress = progress;
  this.serviceId = serviceId;
  this.teeth = teeth;
  this.units = units;
  this.visitId = visitId;
  this.order = order;
  /**
   * @description Сумма.
   * @type {ServicePriceRubles}
   */
  this.amount = new ServicePriceRubles(this.price).mul(this.count);
  if (this.byVHIPolicy) {
    this.discountAmount = this.amount;
    this.finalPrice = ServicePriceRubles.zero();
  } else {
    ({
      discount: this.discountAmount,
      final: this.finalPrice,
    } = this.discount.apply(this.amount));
  }
  const copyWith = (props) => new this.constructor(
    props.byVHIPolicy ?? this.byVHIPolicy,
    this.categoryName,
    props.count ?? this.count,
    props.dentalParts ?? this.dentalParts,
    props.discount ?? this.discount,
    this.id,
    this.key,
    this.price,
    this.progress,
    this.serviceId,
    serviceName,
    props.teeth ?? this.teeth,
    props.units ?? this.units,
    this.visitDate,
    this.visitId,
    this.visitNumber,
    props.order ?? this.order,
  );
  this.setByVHIPolicy = (value) => copyWith({ byVHIPolicy: value });
  this.setCount = (value) => copyWith({ count: value });
  this.setDiscount = (value) => copyWith({ discount: value });
  this.setTeeth = (newTeeth, newDentalParts) => copyWith({
    teeth: newTeeth,
    dentalParts: newDentalParts,
  });
  this.setUnits = (value) => copyWith({ units: value });
  this.setOrder = (value) => copyWith({ order: value });
  this.getVisit = () => ({
    date: visitDate,
    id: visitId,
    title: `№${visitNumber}`,
  });
  this.getService = () => ({
    categoryName,
    title: serviceName,
    units,
    value: serviceId,
  });
}

/**
 * @description Этап плана лечения пациента.
 * @param id
 * @param serviceList
 * @param name
 * @constructor
 */
export function PatientPlanStage(
  id = null,
  serviceList = [],
  name = 'Новый этап',
) {
  this.id = id;
  this.serviceList = serviceList;
  this.name = name;
  this.setName = (value) => new this.constructor(
    this.id,
    this.serviceList,
    value,
  );
  this.setServiceList = (value) => new this.constructor(
    this.id,
    value,
    this.name,
  );
}

/**
 * @description Элемент списка планов лечения пациента.
 * @param {Date}                  date           Дата.
 * @param {string}                discountType
 * @param {number|null}           id             Идентификатор.
 * @param {string}                name           Название.
 * @param {number}                price          Итоговая стоимость.
 * @param {PlanService[]}         serviceList    Услуги.
 * @param {string}                status         Статус.
 * @param {array}                 tags
 * @param {number|null}           templateId     Идентификатор шаблона.
 * @param {string}                templateName
 * @param {PatientPlanStage[]}    stages         Этапы.
 * @param {number|null}           employeeId     Идентификатор ответственного сотрудника.
 * @param {string}                patientName    Имя пациента.
 * @param {Date|null}             effectiveUntil Действует до.
 * @param {string}                doctorId       Врач.
 * @param {array}                 restrictions   Ограничения.
 * @param {number|null}           authorId       Идентификатор автора.
 * @constructor
 */
export function Plan(
  date = new Date(),
  discountType = DISCOUNT_IN_KOPECK,
  id = null,
  name = 'Новый план',
  price = 0,
  serviceList = [],
  status = 'DRAFT',
  tags = [],
  templateId = null,
  templateName = '',
  stages = [],
  employeeId = null,
  patientName = '',
  effectiveUntil = null,
  doctorId = '',
  restrictions = [],
  authorId = null,
) {
  this.restrictions = restrictions;
  this.date = date;
  this.discountType = discountType;
  this.id = id;
  this.name = name;
  this.price = price;
  this.serviceList = serviceList;
  this.status = status;
  this.tags = tags;
  this.templateId = templateId;
  this.stages = stages;
  this.employeeId = employeeId;
  this.patientName = patientName;
  this.effectiveUntil = effectiveUntil;
  this.doctorId = doctorId;
  this.templateName = templateName;
  this.authorId = authorId;
  /**
   * @description Снять текущее состояние в строку.
   * @returns {string}
   */
  const calcMomentState = () => JSON.stringify(this);
  /**
   * @description Исходное состояние.
   * @var {string}
   */
  let initialState = calcMomentState();
  /**
   * @description Применить шаблон.
   * @param {PlanTemplate} template Шаблон.
   * @returns {Plan}
   */
  this.applyTemplate = (template) => {
    const serviceMapper = (service, index) => new PlanService(
      false,
      '',
      service.getCount(),
      service.getDentalParts(),
      new PositionDiscount(this.discountType, 0),
      null,
      `${service.getId()}-${Date.now()}`,
      service.getPrice(),
      '',
      service.getId(),
      service.getName(),
      service.getTeeth(),
      service.getUnits(),
      null,
      null,
      null,
      index,
    );
    const services = template.getServices().map(serviceMapper);
    const stageList = template.getStages().map((stage) => new PatientPlanStage(
      null,
      stage.getServices().map(serviceMapper),
      stage.getName(),
    ));
    const totalPrice = template
      .getServices()
      .map((item) => item.getPrice())
      .reduce((servicePrice, sum) => sum + servicePrice, 0);
    return new this.constructor(
      this.date,
      this.discountType,
      this.id,
      this.name,
      totalPrice,
      [...this.serviceList, ...services].map((service, index) => service.setOrder(index)),
      this.status,
      tags,
      template.getId(),
      template.getName(),
      [...this.stages, ...stageList],
      this.employeeId,
      this.patientName,
      this.effectiveUntil,
      this.doctorId,
      this.restrictions,
      this.authorId,
    );
  };

  /**
   * @description Зафиксировать текущее состояние как исходное.
   * @returns {void}
   */
  this.refresh = () => {
    initialState = calcMomentState();
  };

  /**
   * @description Есть различие с исходным состоянием.
   * @returns {boolean}
   */
  this.isChanged = () => {
    const currentState = calcMomentState();
    return currentState !== initialState;
  };
}

/**
 * @description Визит.
 * @param {number} id     Идентификатор.
 * @param {string} date   Дата визита.
 * @param {string} number Номер визита.
 * @constructor
 */
export function Visit(id, date, number) {
  this.id = id;
  const dateParts = date.split('.');
  dateParts[2] = dateParts[2].slice(2);
  this.date = dateParts.join('.');
  [, this.number] = number.split('№');
}

/**
 * @description Запрос на создание плана лечения.
 * @param {number}      patientId  Идентификатор пациента.
 * @param {Plan}        plan       План лечения.
 * @constructor
 */
export function CreatePlanRequest(patientId, plan) {
  /**
   * @description Получить идентификатор пациента.
   * @returns {number}
   */
  this.getPatientId = () => patientId;

  /**
   * @description Привести к формату запроса.
   * @returns {any}
   */
  this.toQuery = () => {
    const makeService = (service) => ({
      by_vhi_policy: service.byVHIPolicy,
      count: service.count,
      dental_parts: service.dentalParts.map(({ value }) => value),
      discount: service.discount.getValue(),
      service_id: service.serviceId,
      teeth: service.teeth,
      units: service.units,
      visit_id: service.visitId,
    });
    return {
      date: getISODateStringByDate(plan.date),
      discount_type: plan.discountType,
      name: plan.name,
      services: plan.serviceList.map(makeService),
      template_id: plan.templateId,
      employee_id: plan.employeeId,
      status: plan.status,
      stages: plan.stages.map((stage) => ({
        id: stage.id,
        name: stage.name,
        services: stage.serviceList.map(makeService),
      })),
      effective_until: plan.effectiveUntil ? getISODateStringByDate(plan.effectiveUntil) : '',
      doctor_id: plan.doctorId,
    };
  };
}

/**
 * @description Запрос на создание категории.
 * @param {string} name
 * @constructor
 */
export function TreatmentPlanTemplateCategoryCreateRequest(name) {
  this.toQuery = () => ({
    name,
  });
}

/**
 * @description Запрос на создание категории.
 * @param {string} id
 * @param {string} name
 * @constructor
 */
export function TreatmentPlanTemplateCategoryEditRequest(id, name) {
  this.getId = () => id;
  this.toQuery = () => ({
    name,
  });
}

/**
 * @description Запрос на редактирование плана лечения.
 * @param {Plan} plan План лечения.
 * @constructor
 */
export function EditPlanRequest(plan) {
  this.getPlanId = () => plan.id;
  this.toQuery = () => {
    const makeService = (service) => ({
      by_vhi_policy: service.byVHIPolicy,
      count: service.count,
      dental_parts: service.dentalParts.map(({ value }) => value),
      discount: service.discount.getValue(),
      id: service.id,
      service_id: service.serviceId,
      teeth: service.teeth,
      units: service.units,
      visit_id: service.visitId,
      order: service.order,
    });
    return {
      name: plan.name,
      discount_type: plan.discountType,
      date: getISODateStringByDate(plan.date),
      services: plan.serviceList.map(makeService),
      status: plan.status,
      stages: plan.stages.map((stage) => ({
        id: stage.id,
        name: stage.name,
        services: stage.serviceList.map(makeService),
      })),
      template_id: plan.templateId,
      employee_id: plan.employeeId,
      effective_until: plan.effectiveUntil ? getISODateStringByDate(plan.effectiveUntil) : '',
      doctor_id: plan.doctorId,
    };
  };
}

/**
 * @description Запрос шаблона плана лечения.
 * @param {string} name
 * @param {boolean} isPrintLogo
 * @param {array} services
 * @param {array} stages
 * @param {string} categoryId
 * @constructor
 */
export function TemplatePlanRequest(
  name = '',
  isPrintLogo = false,
  services = [],
  stages = [],
  categoryId = '',
) {
  const makeService = (service) => ({
    id: service.code,
    count: service.count,
    teeth: service.teeth,
    price: service.price,
  });
  this.toQuery = () => ({
    name,
    is_print_logo: isPrintLogo,
    services: services.map(makeService),
    stages: stages.map((stage) => ({
      id: stage.code,
      name: stage.name,
      services: stage.services.map(makeService),
    })),
    category_id: categoryId,
  });
}

/**
 * @description Шлюз планов.
 * @param {Axios} axios
 * @constructor
 */
export default function PlanGateway(axios) {
  /**
   * @description Отправить запрос на создание плана лечения пациента.
   * @param {CreatePlanRequest} request Запрос.
   * @returns {Promise<Validation>}
   */
  this.create = async (request) => {
    try {
      const response = await axios.post(
        `/api/patients/${request.getPatientId()}/plans`,
        request.toQuery(),
      );
      const result = {
        message: response.data.message,
        planId: response.data.plan_id,
      };
      return Validation.Success(result);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Создать шаблон для плана лечения.
   * @param {object} data Данные.
   * @returns {Promise<Maybe>}
   */
  this.createTemplate = (data) => axios
    .post('/api/treatment-plan-templates', data)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на удаление плана лечения пациента.
   * @param {number} planId Идентификатор плана лечения.
   * @returns {Promise<Validation>}
   */
  this.delete = async (planId) => {
    try {
      const response = await axios.delete(`/api/plans/${planId}`);
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на удаление услуги из плана лечения пациента.
   * @param {number} planId    Идентификатор плана лечения.
   * @param {number} serviceId Идентификатор услуги.
   * @returns {Promise<Validation>}
   */
  this.deleteService = async (planId, serviceId) => {
    try {
      const response = await axios.delete(`/api/plans/${planId}/services/${serviceId}`);
      return Validation.Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Скачать акт.
   * @param {number} planId              Идентификатор плана.
   * @param {number} employeePerformerId Идентификатор сотрудника исполнителя.
   * @returns {Promise<Either>}
   */
  this.downloadTreatmentPlan = (planId, employeePerformerId) => axios
    .post(
      `/api/plans/${planId}/form`,
      { employee_performer_id: employeePerformerId },
      { headers: { accept: ACCEPT_DOCX }, responseType: 'blob' },
    ).then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          return Either.left(data);
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на редактирование плана лечения пациента.
   * @param {EditPlanRequest} request Запрос.
   * @returns {Promise<Validation>}
   */
  this.edit = async (request) => {
    try {
      const response = await axios.put(
        `/api/plans/${request.getPlanId()}`,
        request.toQuery(),
      );
      const { message } = response.data;
      return Validation.Success({ message });
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отредактировать шаблон.
   * @param {object} data       Данные.
   * @param {number} templateId Идентификатор шаблона.
   * @returns {Promise<Either>}
   */
  this.editTemplate = (data, templateId) => axios
    .put(`/api/treatment-plan-templates/${templateId}`, data)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить доступные статусы.
   * @return {Promise<any[]>}
   */
  this.getAllStatuses = () => Promise.resolve(
    [
      {
        text: 'Черновик',
        value: 'DRAFT',
      },
      {
        text: 'На согласовании',
        value: 'TO_BE_AGREED',
      },
      {
        text: 'Согласован',
        value: 'ACCEPTED_FOR_WORK',
      },
      {
        text: 'Отклонен пациентом',
        value: 'CANCELED_BY_PATIENT',
      },
      {
        text: 'В работе',
        value: 'IN_WORK',
      },
      {
        text: 'Выполнен',
        value: 'COMPLETED',
      },
    ],
  );

  /**
   * @description Получить список сотрудников для назначения ответсвенным.
   * @return {Promise<any[]>}
   */
  this.getResponsibleEmployeeList = () => axios
    .get('/api/plans/employees')
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError) {
          if (error.response && [400, 403, 404].includes(error.response.status)) {
            return Either.left(error.response.data);
          }
          if (!error.response) {
            throw new Error('Network error: сервер по адресу /api/plans/employees не ответил на запрос');
          }
        }
        throw error;
      },
    );

  /**
   * @description Создать категорию
   * @param {TreatmentPlanTemplateCategoryCreateRequest} request
   * @returns {Promise<Maybe<any[]>>}
   */
  this.createCategory = (request) => axios
    .post('/api/treatment-plan-template/category', request.toQuery())
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать категорию
   * @param {TreatmentPlanTemplateCategoryEditRequest} request
   * @returns {Promise<Maybe<any[]>>}
   */
  this.editCategory = (request) => axios
    .put(`/api/treatment-plan-template/category/${request.getId()}`, request.toQuery())
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать категорию
   * @param {string} id
   * @returns {Promise<Maybe<any[]>>}
   */
  this.deleteCategory = (id) => axios
    .delete(`/api/treatment-plan-template/category/${id}`)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить категории.
   * @returns {Promise<Maybe<any[]>>}
   */
  this.getCategories = () => axios
    .get('/api/treatment-plan-template/categories')
    .then((response) => Maybe.fromEmpty(response.data))
    .then((result) => result.map(({ data }) => data
      .map((category) => new PlanCategory(category.id, category.name))));

  /**
   * @description Получить шаблоны.
   * @param categoryId {string}
   * @returns {Promise<Maybe<any[]>>}
   */
  this.getTemplates = (categoryId) => axios
    .get(`/api/treatment-plan-templates?category_id=${categoryId}`)
    .then((response) => Maybe.fromEmpty(response.data))
    .then((result) => result.map(({ data }) => data.map(
      (template) => new PlanTemplate(
        template.id,
        template.has_trashed,
        template.is_print_logo,
        template.name,
        (template.services || []).map((service) => new PlanTemplateService(
          service.count,
          service.dental_parts,
          service.id,
          service.name,
          service.price,
          service.teeth,
          service.units,
          service.category_name,
        )),
        (template.stages || []).map((stage) => new PlanTemplateStage(
          stage.id,
          stage.name,
          (stage.services || []).map((service) => new PlanTemplateService(
            service.count,
            service.dental_parts,
            service.id,
            service.name,
            service.price,
            service.teeth,
            service.units,
            service.category_name,
          )),
        )),
        template.category_id,
      ),
    )));

  /**
   * @description Удалить шаблон.
   * @param {number} templateId Идентификатор шаблона.
   * @returns {Promise<Either>}
   */
  this.removeTemplate = (templateId) => axios
    .delete(`/api/treatment-plan-templates/${templateId}`)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список планов лечения пациента.
   * @param {number} patientId Идентификатор пациента.
   * @param {any}    query     Запрос.
   * @returns {Promise<Validation>}
   */
  this.viewList = async (patientId, query = {}) => {
    const params = {};
    if (query.filterByStatus && query.filterByStatus !== 'ALL') {
      params.status = query.filterByStatus;
    }
    if (query.sortBy && query.orderBy) {
      params.sort_type = query.sortBy;
      params.sort_order = query.orderBy;
    }
    const requestUrl = `/api/patients/${patientId}/plans`;
    try {
      const response = await axios
        .get(
          requestUrl,
          { params },
        );
      const list = response.data.list.map(
        (item) => new PlanItem(
          item.author_name,
          item.author_speciality,
          item.branch_name,
          item.id,
          item.name,
          item.date,
          item.price,
          item.status_id,
          item.has_services_from_another_branch,
        ),
      );
      const data = {
        list,
        acceptToWorkCount: response.data.accept_to_work_count,
        cancelByPatientCount: response.data.cancel_by_patient_count,
        completedCount: response.data.completed_count,
        draftCount: response.data.draft_count,
        inWorkCount: response.data.in_work_count,
        toBeAgreedCount: response.data.to_be_agreed_count,
        branchName: response.data.branch_name,
      };
      return Validation.Success(data);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response) {
          return Validation.Fail(error.response.data);
        }
        throw new Error(`Network error: сервер по адресу ${requestUrl} не ответил на запрос`);
      }
      throw error;
    }
  };

  /**
   * @description Получить данные плана лечения пациента.
   * @param {number} planId Идентификатор плана лечения.
   * @returns {Promise<Validation>}
   */
  this.viewById = async (planId) => {
    try {
      const response = await axios.get(`/api/plans/${planId}`);
      const discountType = response.data.discount_type;
      const makeService = (service) => new PlanService(
        service.by_vhi_policy,
        service.category_name,
        service.count,
        service.dental_parts,
        new PositionDiscount(discountType, service.discount),
        service.id,
        '',
        service.price,
        service.progress,
        service.service_id,
        service.service_name,
        service.teeth,
        service.units,
        service.visit_date,
        service.visit_id,
        service.visit_number,
        service.order,
      );
      const { template } = response.data;
      const effectiveUntil = response.data.effective_until;
      const plan = new Plan(
        new Date(response.data.date),
        discountType,
        response.data.id,
        response.data.name,
        response.data.price,
        response.data.services.map(makeService),
        response.data.status,
        response.data.tags,
        template?.id ?? null,
        template?.name ?? '',
        response.data.stages.map((stage) => new PatientPlanStage(
          stage.id,
          stage.services.map(makeService),
          stage.name,
        )),
        response.data.employee_id,
        response.data.patient_name,
        effectiveUntil ? new Date(effectiveUntil) : null,
        response.data.doctor_id,
        response.data.restrictions,
        response.data.author_id,
      );
      return Validation.Success({ plan });
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Распечатать по идентификатору плана.
   * @param {number} planId              Идентификатор плана.
   * @param {number} employeePerformerId Идентификатор сотрудника исполнителя.
   * @returns {Promise<Either>}
   */
  this.printTreatmentPlan = (planId, employeePerformerId) => axios
    .post(
      `/api/plans/${planId}/form`,
      { employee_performer_id: employeePerformerId },
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    ).then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные для заполнения плана.
   * @param {number|null} planId
   * @returns {Maybe}
   */
  this.viewTreatmentPlanFillers = (planId = null) => axios
    .get('/api/treatment-plan-fillers', { params: { plan_id: planId } })
    .then((response) => Maybe.fromEmpty(response.data.data))
    .then((result) => result.map((data) => {
      const makeService = (service) => ({
        categoryId: service.category_id,
        categoryName: service.category_name,
        cost: service.cost,
        duration: service.duration,
        id: service.id,
        name: service.name,
        units: service.units,
      });
      const makeCategoryList = (category) => (new Category(
        category.id,
        category.name,
        category.services.map(makeService),
        category.children,
      ));
      const priceLists = (data.prices || [])
        .map((price) => new PriceList(
          price.id,
          price.name,
          price.categories.map((category) => makeCategoryList(category)),
        ));
      const serviceMapper = (service) => new PlanTemplateService(
        service.count,
        service.dental_parts,
        service.id,
        service.name,
        service.price,
        service.teeth,
        service.units,
        service.category_name,
      );
      const templates = data.templates.map(
        (template) => new PlanTemplate(
          template.id,
          template.has_trashed,
          template.is_print_logo,
          template.name,
          (template.services || []).map(serviceMapper),
          (template.stages || []).map((stage) => new PlanTemplateStage(
            stage.id,
            stage.name,
            (stage.services || []).map(serviceMapper),
          )),
          template.category_id,
        ),
      );
      const periodTypes = data.period_types.map(
        ({ from_to: [from, to], title, value }) => ({
          periodDates: [new Date(from), new Date(to)],
          title,
          value,
        }),
      );
      const defaultDate = {
        from: new Date(data.default_date.from),
        to: new Date(data.default_date.to),
      };
      return {
        templates,
        priceLists,
        periodTypes,
        defaultDate,
        doctorList: data.doctors,
        initialDoctorId: data.initial_doctor_id,
      };
    }));
  /**
   * @description Изменить категорию документа.
   * @param {string} planId
   * @returns {Promise<Either>}
   */
  this.duplicatePlan = (planId) => axios
    .post(`/api/plans/${planId}/duplicate`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
